import { ChangeEvent, HTMLInputTypeAttribute, MutableRefObject, ReactNode, useRef } from 'react'
import _ from 'lodash'
import styled, { CSSObject } from '@emotion/styled'
import { cid24 } from '@fusion-advantage/formlib'
import { FusionColor } from '../../../theme/FusionStyle'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ComplexElementSize } from '../ComplexElementSize'

// re-design reference material:
// ComplexElementSize.tsx
// ComplexElementSizeComponents.tsx

interface L1TextFieldRootProps {
	// the preferred sizing of the whole box
	// including tip and units
	// is specified here
	size: ComplexElementSize
}

const L1TextFieldRoot = styled.div<L1TextFieldRootProps>(({theme, size}): CSSObject => {
	const style: CSSObject = {
		boxSizing: 'border-box',
		// allow the box to size more like a plain <input /> element
		// when in a plain flow context
		display: 'inline-flex',
		flexFlow: 'row nowrap',
		alignItems: 'center',
		columnGap: theme.spacing(1),
		// the addition of maxWidth causes the field to gently size down
		// when the width specified exceeds the bounds of the space provided
		// in L2ControlGrid.  it happens a lot when cramming a bunch of stuff
		// on the form and viewing on iPad.
		maxWidth: '100%',
	}
	switch(size) {
		case ComplexElementSize.Undefined:
			// by default keep intrinsic size as minimal as possible
			// for maximum flexibility
			break
		case ComplexElementSize.Min:
			style.width = theme.spacing(20)
			break
		case ComplexElementSize.Medium:
			style.width = theme.spacing(30)
			break
		case ComplexElementSize.Max:
			style.width = theme.spacing(45)
			break
		case ComplexElementSize.FlexStretch:
			style.flex = '1 1 auto'
			break
	}
	return style
})

// establish a containing block
// for the absolute overlay to position itself
// https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block
const TextFieldControlContainer = styled.div({
	flex: '1 1 auto',
	position: 'relative',
	display: 'flex',
})

interface TextFieldControlProps {
	leftIcon: boolean
	rightIcon: boolean
	borderColor?: FusionColor
}

const TextFieldControl = styled.input<TextFieldControlProps>(({theme, leftIcon, rightIcon, borderColor}) => ({
	flex: '1 1 auto',
	backgroundColor: theme.colors.white,
	border: `1px solid ${borderColor ? theme.colors[borderColor] : theme.colors.lines}`,
	borderRadius: theme.borderRadius,
	padding: theme.spacing(1),
	paddingLeft: theme.spacing(leftIcon ? 4 : 1),
	paddingRight: theme.spacing(rightIcon ? 4 : 1),
	fontSize: '1rem',
	'::placeholder': {
		color: theme.colors[FusionColor.MiniHeader]
	},

	// if we don't specify width, in safari, it ends up about ~145px
	// and can overflow the bounds of the Root box when space is tight.
	// so it seems like there should be a low default here to allow the
	// box to size way down, and let the parent container size it back up
	// to the ideal width for the space.
	boxSizing: 'border-box',
	width: theme.spacing(4),
}))

// print-only version that displays all of the available content
const TextFieldViewOnlyControl = TextFieldControl.withComponent('div')

const TextFieldControlOverlay = styled.div({
	position: 'absolute',
	left: 0,
	top: 0,
	width: '100%',
	height: '100%',
	zIndex: 2,
	pointerEvents: 'none',
	display: 'flex',
	flowFlow: 'row nowrap',
	justifyContent: 'space-between',
	alignItems: 'center',
})

type TextFieldIconProps = {
	color?: FusionColor
}

const TextFieldIcon = styled.div<TextFieldIconProps>(({theme, color = FusionColor.BlueMain}) => ({
	padding: theme.spacing(0, 1),
	color: theme.colors[color]
}))

const TextFieldDecoration = styled.div()

interface L1TextFieldProps {
	className?: string
	inputType?: HTMLInputTypeAttribute,
	size?: ComplexElementSize,
	borderColor?: FusionColor,
	placeholder?: string,
	value?: string,
	disabled?: boolean,
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void,
	units?: string|null,
	tip?: ReactNode,
	prefixIcon?: IconProp,
	suffixIcon?: IconProp,
	prefixIconColor?: FusionColor,
	suffixIconColor?: FusionColor,
	inputRef?: MutableRefObject<any>
	viewOnly?: boolean
}

const L1TextField = ({
	className,
	inputType = 'text',
	// for backwards compatibility, this remains the default size
	// use .Undefined when a grid template column will size this component
	size = ComplexElementSize.Medium,
	borderColor,
	value,
	placeholder,
	disabled = false,
	onChange,
	units,
	tip,
	prefixIcon,
	suffixIcon,
	prefixIconColor,
	suffixIconColor,
	inputRef,
	viewOnly,
}: L1TextFieldProps) => {
	const idRef = useRef(cid24())
	if(_.isString(units) && units.trim() === '') {
		units = null
	}
	const extraInputProps = {}
	if(inputType === 'number') {
		// inputType number causes a number-optimized keyboard on iOS
		// but none of these seem to make a difference anymore
		// https://stackoverflow.com/q/14447668
		// extraInputProps.pattern = '\\d*'
		// extraInputProps.inputMode = 'numeric'
	}
	const Control = viewOnly ? TextFieldViewOnlyControl : TextFieldControl
	return (
		<L1TextFieldRoot className={className} size={size}>
			<TextFieldControlContainer>
				<Control
					ref={inputRef}
					borderColor={borderColor}
					id={idRef.current}
					type={inputType}
					placeholder={placeholder}
					value={value ? value : ''}
					children={viewOnly ? (value ? value : "\u00A0") : null}
					disabled={disabled}
					onChange={onChange}
					{...extraInputProps}
					leftIcon={Boolean(prefixIcon)}
					rightIcon={Boolean(suffixIcon)}
				/>
				<TextFieldControlOverlay>
					{/* these icons for decoration only;
					    use L1SmallIconButton as `tip` prop for clickable decoration */}
					<TextFieldIcon color={prefixIconColor}>{prefixIcon ? <FontAwesomeIcon icon={prefixIcon} /> : null}</TextFieldIcon>
					<TextFieldIcon color={suffixIconColor}>{suffixIcon ? <FontAwesomeIcon icon={suffixIcon} /> : null}</TextFieldIcon>
				</TextFieldControlOverlay>
			</TextFieldControlContainer>
			{units ? <TextFieldDecoration><label htmlFor={idRef.current}>{units}</label></TextFieldDecoration> : null}
			{tip ? <TextFieldDecoration>{tip}</TextFieldDecoration> : null}
		</L1TextFieldRoot>
	) 
}

export default L1TextField
